import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { X, Pencil, KeyRound, UserX, UserCheck, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { usuariosAPI } from '../services/api';
import { selectStyles } from '../utils/adminShared';

const rolOptions = [
    { value: 'superadmin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'gestor_proyecto', label: 'Gestor de Proyectos' },
    { value: 'comercial', label: 'Comercial' },
    { value: 'personal', label: 'Personal' },
];

const rolLabel = (rol) => rolOptions.find(r => r.value === rol)?.label || rol;

const UsuariosPanel = () => {
    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [nuevoUsuario, setNuevoUsuario] = useState({ nombre: '', email: '', password: '', rol: 'personal' });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPass, setShowNewPass] = useState(false);
    const [editUsuario, setEditUsuario] = useState(null);
    const [passUsuario, setPassUsuario] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmResetPass, setConfirmResetPass] = useState('');
    const [showPassModal, setShowPassModal] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const res = await usuariosAPI.getAll();
            setUsuarios(res.data || []);
        } catch (err) {
            console.error('Error al cargar usuarios:', err);
        } finally {
            setLoading(false);
        }
    };

    const agregarUsuario = async () => {
        if (!nuevoUsuario.nombre.trim() || !nuevoUsuario.email.trim() || !nuevoUsuario.password) {
            toast.error('Todos los campos son obligatorios');
            return;
        }
        if (nuevoUsuario.password !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        try {
            await usuariosAPI.create(nuevoUsuario);
            setNuevoUsuario({ nombre: '', email: '', password: '', rol: 'personal' });
            setConfirmPassword('');
            setShowNewPass(false);
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const toggleUsuarioActivo = async (id, activo) => {
        try {
            await usuariosAPI.update(id, { activo: !activo });
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const guardarEditUsuario = async () => {
        if (!editUsuario) return;
        if (!editUsuario.nombre.trim() || !editUsuario.email.trim()) {
            toast.error('Nombre y email son obligatorios');
            return;
        }
        try {
            await usuariosAPI.update(editUsuario.id, {
                nombre: editUsuario.nombre,
                email: editUsuario.email,
                rol: editUsuario.rol
            });
            toast.success('Usuario actualizado');
            setEditUsuario(null);
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const generarPassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
        let pass = '';
        for (let i = 0; i < 12; i++) pass += chars[Math.floor(Math.random() * chars.length)];
        return pass;
    };

    const guardarPassword = async () => {
        if (!passUsuario || !newPassword) {
            toast.error('La contraseña es obligatoria');
            return;
        }
        if (newPassword !== confirmResetPass) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        try {
            await usuariosAPI.resetPassword(passUsuario.id, newPassword);
            toast.success('Contraseña actualizada');
            setPassUsuario(null);
            setNewPassword('');
            setConfirmResetPass('');
            setShowPassModal(false);
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    if (loading) {
        return <div className="loading">Cargando usuarios...</div>;
    }

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Usuarios</h2>
            </div>
            <div className="admin-content">
                <h3 className="h3">Agregar Usuario</h3>
                <div className="form-row">
                    <label className="field">
                        <span className="label">Nombre *</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoUsuario.nombre}
                            onChange={(e) => setNuevoUsuario(n => ({ ...n, nombre: e.target.value }))}
                        />
                    </label>
                    <label className="field">
                        <span className="label">Email *</span>
                        <input
                            className="input"
                            type="email"
                            value={nuevoUsuario.email}
                            onChange={(e) => setNuevoUsuario(n => ({ ...n, email: e.target.value }))}
                        />
                    </label>
                    <label className="field">
                        <span className="label">Contraseña *</span>
                        <div className="input-password-wrap">
                            <input
                                className="input"
                                type={showNewPass ? 'text' : 'password'}
                                value={nuevoUsuario.password}
                                onChange={(e) => setNuevoUsuario(n => ({ ...n, password: e.target.value }))}
                                placeholder="Mínimo 8 caracteres"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowNewPass(!showNewPass)}
                                tabIndex={-1}
                            >
                                {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </label>
                    <label className="field">
                        <span className="label">Confirmar contraseña *</span>
                        <div className="input-password-wrap">
                            <input
                                className="input"
                                type={showNewPass ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repetir contraseña"
                            />
                        </div>
                    </label>
                    <button
                        type="button"
                        className="btn ghost"
                        onClick={() => {
                            const pass = generarPassword();
                            setNuevoUsuario(n => ({ ...n, password: pass }));
                            setConfirmPassword(pass);
                            setShowNewPass(true);
                        }}
                        style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
                        title="Generar contraseña segura"
                    >
                        <RefreshCw size={16} /> Generar
                    </button>
                    <label className="field">
                        <span className="label">Rol</span>
                        <Select
                            styles={selectStyles}
                            options={rolOptions}
                            value={rolOptions.find(r => r.value === nuevoUsuario.rol)}
                            onChange={(opt) => setNuevoUsuario(n => ({ ...n, rol: opt.value }))}
                            isSearchable={false}
                        />
                    </label>
                    <button className="btn neon" type="button" onClick={agregarUsuario}>
                        + Agregar
                    </button>
                </div>

                <div className="divider" />

                <h3 className="h3">Usuarios Existentes</h3>
                <div className="tableWrap">
                    <table className="galTable">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Email</th>
                                <th>Rol</th>
                                <th>Estado</th>
                                <th>Ultimo acceso</th>
                                <th style={{ textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usuarios.map(u => (
                                <tr key={u.id} className={!u.activo ? 'inactive' : ''}>
                                    <td>{u.nombre}</td>
                                    <td>{u.email}</td>
                                    <td>
                                        <span className={`badge ${u.rol === 'superadmin' ? 'warning' : u.rol === 'admin' ? 'success' : 'info'}`}>
                                            {rolLabel(u.rol)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${u.activo ? 'success' : 'danger'}`}>
                                            {u.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td>{u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleString() : '-'}</td>
                                    <td>
                                        <div className="acciones-btns" style={{ justifyContent: 'center' }}>
                                            <button
                                                className="btn-icon edit"
                                                title="Editar usuario"
                                                onClick={() => setEditUsuario({ id: u.id, nombre: u.nombre, email: u.email, rol: u.rol || 'personal' })}
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                className="btn-icon password"
                                                title="Cambiar contraseña"
                                                onClick={() => { setPassUsuario(u); setNewPassword(''); }}
                                            >
                                                <KeyRound size={16} />
                                            </button>
                                            <button
                                                className={`btn-icon ${u.activo ? 'text-danger' : 'text-success'}`}
                                                title={u.activo ? 'Desactivar' : 'Activar'}
                                                onClick={() => toggleUsuarioActivo(u.id, u.activo)}
                                            >
                                                {u.activo ? <UserX size={16} /> : <UserCheck size={16} />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        {/* Modal: Editar Usuario */}
        {editUsuario && (
            <div className="modal-overlay" onClick={() => setEditUsuario(null)}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3 className="h3">Editar Usuario</h3>
                        <button
                            className="btn-icon"
                            onClick={() => setEditUsuario(null)}
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <label className="field">
                            <span className="label">Nombre *</span>
                            <input
                                className="input"
                                type="text"
                                value={editUsuario.nombre}
                                onChange={(e) => setEditUsuario(prev => ({ ...prev, nombre: e.target.value }))}
                            />
                        </label>
                        <label className="field">
                            <span className="label">Email *</span>
                            <input
                                className="input"
                                type="email"
                                value={editUsuario.email}
                                onChange={(e) => setEditUsuario(prev => ({ ...prev, email: e.target.value }))}
                            />
                        </label>
                        <label className="field">
                            <span className="label">Rol</span>
                            <Select
                                styles={selectStyles}
                                options={rolOptions}
                                value={rolOptions.find(r => r.value === editUsuario.rol)}
                                onChange={(opt) => setEditUsuario(prev => ({ ...prev, rol: opt.value }))}
                                isSearchable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </label>

                        <div className="divider" />

                        <div className="form-row" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn ghost" onClick={() => setEditUsuario(null)}>Cancelar</button>
                            <button className="btn neon" onClick={guardarEditUsuario}>Guardar</button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Modal: Cambiar Contraseña */}
        {passUsuario && (
            <div className="modal-overlay" onClick={() => { setPassUsuario(null); setNewPassword(''); setConfirmResetPass(''); setShowPassModal(false); }}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3 className="h3">Cambiar Contraseña</h3>
                        <button
                            className="btn-icon"
                            onClick={() => { setPassUsuario(null); setNewPassword(''); setConfirmResetPass(''); setShowPassModal(false); }}
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <p className="empty-message">
                            Usuario: <strong>{passUsuario.nombre}</strong>
                        </p>
                        <label className="field">
                            <span className="label">Nueva contraseña *</span>
                            <div className="input-password-wrap">
                                <input
                                    className="input"
                                    type={showPassModal ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 8 caracteres"
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassModal(!showPassModal)}
                                    tabIndex={-1}
                                >
                                    {showPassModal ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </label>
                        <label className="field">
                            <span className="label">Confirmar contraseña *</span>
                            <div className="input-password-wrap">
                                <input
                                    className="input"
                                    type={showPassModal ? 'text' : 'password'}
                                    value={confirmResetPass}
                                    onChange={(e) => setConfirmResetPass(e.target.value)}
                                    placeholder="Repetir contraseña"
                                />
                            </div>
                        </label>
                        <button
                            type="button"
                            className="btn ghost"
                            onClick={() => {
                                const pass = generarPassword();
                                setNewPassword(pass);
                                setConfirmResetPass(pass);
                                setShowPassModal(true);
                            }}
                            style={{ alignSelf: 'flex-start' }}
                            title="Generar contraseña segura"
                        >
                            <RefreshCw size={16} /> Generar
                        </button>

                        <div className="divider" />

                        <div className="form-row" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn ghost" onClick={() => { setPassUsuario(null); setNewPassword(''); setConfirmResetPass(''); setShowPassModal(false); }}>Cancelar</button>
                            <button className="btn neon" onClick={guardarPassword}>Cambiar</button>
                        </div>
                    </div>
                </div>
            </div>
        )}
    </>
    );
};

export default UsuariosPanel;
